import { ModuleThumb, EyeThumb } from "./QrThumbs"

// Module + finder-pattern (eye) shape pickers. Mirrors FrameSelect's idiom
// (thumbnail radios, voltage selection ring) but is a Pro upsell: when
// `locked`, only the square default is selectable and every other option shows
// a lock that routes to the upgrade flow. Server-side the write is gated too
// (Entitlements#custom_shapes?), so this is the affordance, not the enforcement.
//
// The thumbnails moved to QrThumbs when the mobile customiser needed the same
// drawings. They are the same paths at the same default size, so this picker
// renders exactly as it did — the extraction exists so the two surfaces can't
// drift into showing different pictures of the same option.

const MODULE_SHAPES = [
  { value: "square",  label: "Square" },
  { value: "rounded", label: "Rounded" },
  { value: "dot",     label: "Dots" },
  { value: "fluid",   label: "Fluid" }
]

const EYE_SHAPES = [
  { value: "square",  label: "Square" },
  { value: "rounded", label: "Rounded" },
  { value: "leaf",    label: "Leaf" },
  { value: "circle",  label: "Circle" }
]

function LockBadge() {
  return (
    <span className="absolute top-1 right-1 text-plasma dark:text-plasma-light" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    </span>
  )
}

function ShapeGroup({ legend, options, value, locked, onChange, onUpgrade, Thumb, cols }) {
  return (
    <fieldset>
      <legend className="block text-xs font-semibold mb-1.5 text-[color:var(--color-muted)] dark:text-muted-light">{legend}</legend>
      <div className={`grid gap-2 ${cols}`}>
        {options.map((o) => {
          const selected = (value || "square") === o.value
          const isLocked = locked && o.value !== "square"
          return (
            <label
              key={o.value}
              className={
                // min-w-0 lets the cell shrink to its grid track so a wide label
                // can't push past the button border (flex items default to
                // min-width:auto, which was the overflow).
                "relative flex flex-col items-center gap-1 p-2 rounded border cursor-pointer transition text-center min-w-0 " +
                (selected
                  ? "border-voltage bg-voltage/5"
                  : "border-[color:var(--color-border)] dark:border-storm hover:border-voltage") +
                (isLocked ? " opacity-60" : "")
              }
            >
              <input
                type="radio"
                name={legend}
                value={o.value}
                checked={selected}
                // Locked options are the Pro upsell: route to the upgrade flow.
                // Fall back to /pricing when no handler is wired (the anonymous
                // marketing mount passes none) so the click always goes somewhere.
                onChange={() =>
                  isLocked
                    ? (onUpgrade ? onUpgrade("shapes") : window.location.assign("/pricing"))
                    : onChange(o.value)
                }
                className="sr-only"
              />
              {isLocked && <LockBadge />}
              <Thumb shape={o.value} />
              <span className="w-full text-center text-[11px] font-semibold leading-tight">{o.label}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

export default function ShapeSelect({ moduleShape, eyeShape, onChange, locked = false, onUpgrade }) {
  const set = (patch) => onChange({ moduleShape: moduleShape || "square", eyeShape: eyeShape || "square", ...patch })

  return (
    // Eyes always wrap below Modules (single column). The 4-up Eyes row needs
    // the full component width to breathe; side-by-side crushed its labels at
    // in-between widths, so we stack unconditionally.
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-5 items-start">
        <ShapeGroup
          legend="Modules"
          options={MODULE_SHAPES}
          value={moduleShape}
          locked={locked}
          onChange={(v) => set({ moduleShape: v })}
          onUpgrade={onUpgrade}
          Thumb={ModuleThumb}
          cols="grid-cols-4"
        />
        <ShapeGroup
          legend="Eyes"
          options={EYE_SHAPES}
          value={eyeShape}
          locked={locked}
          onChange={(v) => set({ eyeShape: v })}
          onUpgrade={onUpgrade}
          Thumb={EyeThumb}
          cols="grid-cols-4"
        />
      </div>
      {locked && (
        <button
          type="button"
          onClick={() => (onUpgrade ? onUpgrade("shapes") : window.location.assign("/pricing"))}
          className="text-xs font-semibold text-plasma dark:text-plasma-light hover:underline"
        >
          Shapes are a Pro feature. Get Pro to use them.
        </button>
      )}
    </div>
  )
}
